import { z } from 'zod';

/// Sunucu env şeması. Uygulama açılışta bunu doğrular ve eksik/bozuk
/// değişkende AÇILMAZ — yarım yapılandırmayla çalışıp sonra 3'üncü günde
/// patlamasındansa hemen ölmesi iyidir.
/**
 * `.env`deki `KEY=` satırı boş DİZE üretir, `undefined` değil.
 *
 * Fark, alan bir BİÇİM doğruluyorsa (regex, e-posta) ölümcül: `.optional()`
 * yalnız `undefined`ı geçiriyor, boş dize şemaya çarpıp uygulamayı AÇILIŞTA
 * düşürüyor. Yani `.env.example`taki "boş bırakabilirsin" talimatı,
 * kopyalayan kişi için çalışmayan bir kurulum demekti.
 *
 * `z.string().optional()` alanlarda sorun yok (boş dize geçerli bir dize) —
 * bu yüzden yalnız biçim doğrulayan alanlarda kullanılıyor.
 */
function emptyToUndefined(value: unknown): unknown {
  return typeof value === 'string' && value.trim().length === 0 ? undefined : value;
}

export const serverEnvSchema = z.object({
  NODE_ENV: z.enum(['development', 'test', 'production']).default('development'),

  DATABASE_URL: z.string().startsWith('postgresql://'),
  REDIS_URL: z.string().startsWith('redis://'),

  API_PORT: z.coerce.number().int().positive().default(3040),
  WORKER_METRICS_PORT: z.coerce.number().int().positive().default(3042),

  // Tarayıcının gördüğü adresler. API ayrı alan adında (api.mentorivo.com);
  // WEB_URL hem CORS izin listesi hem de e-postadaki bağlantıların kökü.
  WEB_URL: z.url(),
  API_PUBLIC_URL: z.url(),

  /**
   * Mağaza webhook'larını platformlara KAYDETMEK açık mı (22 Eyl 2026).
   *
   * 🔴 Varsayılan KAPALI ve sebebi dışarıda: `api.mentorivo.com`un IP kısıtı
   * (bakım modu) dışarıdan gelen teslimatı 503'lüyor. Kısıt dururken kayıt
   * açmak mağazalara ölü bir adres bırakıyor ve platformlar bunu CEZALANDIRIYOR:
   * İkas %70+ hata oranında aboneliği KALICI engelliyor, Trendyol pasife alıp
   * satıcıya e-posta atıyor. Kapalıyken `StoreWebhooksService.enable` hiçbir
   * platforma istek atmıyor; mağazalar yoklamayla canlı (izlenirken 60 sn).
   *
   * Açmak: nginx'te `/commerce/<p>/webhook` yolu dışarıya açıldıktan SONRA
   * `.env`e `STORE_WEBHOOKS_ENABLED=true`, API restart, sonra
   * `/root/plesk-ops/mentorivo/store-webhooks.js enable all`.
   */
  /**
   * 🔴 TEST KARTI — GEÇİCİ, CANLIYA GEÇMEDEN KAPAT (23 Eyl 2026).
   *
   * Açıkken test kartı numaralarıyla (`1111 1111 1111 1117`,
   * `4111 1111 1111 1111`) yapılan ödeme iyzico'ya HİÇ gitmiyor: deneme
   * anında "başarılı" sayılıp plan/kredi teslim ediliyor (para çekilmeden).
   * Kullanıcı kararı: `.env` canlıyken panel akışını denemek için. Bakım modu
   * (yalnız ekip IP'leri) dururken açık; site herkese açılmadan ÖNCE `false`
   * yapılmalı ya da satır silinmeli.
   *
   * 1 Eki 2026'dan beri kapsam: BIN sorgusu, kart ekleme, kayıtlı test
   * kartıyla ödeme ve otomatik yenileme. Test kartı `payment_cards`a SAHTE
   * jetonla (`TEST-…`) kaydediliyor. Bayrak KAPANINCA o kart hiçbir yolda
   * kullanılamaz (`TEST_CARD_DISABLED`, yenilemede `NO_CARD`) ve sahte jeton
   * iyzico'ya gitmez — kartlar elle silinebilir.
   * Kod: `apps/api/src/billing/pay/test-card.ts` (kurallar) ve
   * `pay.service.ts` → `simulateTestCharge`.
   */
  PAY_TEST_CARD_ENABLED: z
    .enum(['true', 'false'])
    .default('false')
    .transform((value) => value === 'true'),

  /**
   * YABANCI KARTTAN USD TAHSİLAT (15 Eki 2026, iyzico "Multi Currency").
   *
   * Açıkken Türkiye dışında çıkarılmış kart kurla çevrilmeden USD'den
   * çekilir; yerli kart her durumda TL (kural `chargeCurrencyFor`,
   * `billing-pay.ts`). Kapalıyken bugünkü gibi her kart TL.
   *
   * 15 Eki 2026'dan beri AÇIK (kullanıcı açtı). Bayrak tek başına USD
   * çekmiyor: API iyzico hesabında USD POS'u olup olmadığını saatte bir
   * kartsız sınıyor (`PayService.foreignCurrencyLive`, checkout form
   * başlatma → `5185` = yok). POS yokken herkes TL görür ve TL öder; iyzico
   * açınca en geç bir saatte yabancı kart USD'ye geçer.
   * Mesafeli satış / ön bilgilendirme / teslimat-iade metinleri hâlâ
   * "tahsilat TL" diyor — USD başladığında çelişir (`/backstage/legal`,
   * kullanıcı kararı).
   */
  PAY_MULTI_CURRENCY_ENABLED: z
    .enum(['true', 'false'])
    .default('false')
    .transform((value) => value === 'true'),

  STORE_WEBHOOKS_ENABLED: z
    .enum(['true', 'false'])
    .default('false')
    .transform((value) => value === 'true'),

  /**
   * Abonelik hatırlatma zinciri PANEL DIŞINA (e-posta, SMS, WhatsApp…) gitsin
   * mi (1 Eki 2026, worker `subscription-reminder.service.ts`).
   *
   * 🔴 Varsayılan KAPALI ve sebebi `STORE_WEBHOOKS_ENABLED` ile aynı: bakım
   * modu. Site yalnız ekip IP'lerine açıkken "aboneliğini yeniden başlat"
   * postasındaki bağlantı alıcıya 503 sayfası açardı. Kapalıyken zincir
   * YİNE çalışıyor ama yalnız UYGULAMA İÇİ (zil) — kritik tipteki aşama
   * (`billing.grace_ended`, kanalı daraltılamıyor) atlanıyor.
   *
   * Açmak: bakım modu kalkarken `.env`e `BILLING_REMINDER_EMAILS_ENABLED=true`
   * + worker restart.
   */
  BILLING_REMINDER_EMAILS_ENABLED: z
    .enum(['true', 'false'])
    .default('false')
    .transform((value) => value === 'true'),

  // Çerezin yazıldığı alan adı. Başında nokta olmalı (`.mentorivo.com`) —
  // web ve API farklı alt alan adlarında olduğu için host'a özel çerez
  // ikisinden yalnızca birine gider. Yerel geliştirmede boş bırakılırsa
  // çerez host'a özel yazılır, o da tek origin'de çalışır.
  COOKIE_DOMAIN: z.string().optional(),

  JWT_SECRET: z.string().min(32, 'JWT_SECRET en az 32 karakter olmalı'),
  JWT_ACCESS_TTL: z.string().default('15m'),

  /**
   * ATIL kalma sınırı — oturum bu kadar süre hiç yenilenmezse ölüyor.
   *
   * Refresh jetonunun ömrü bu; her yenilemede sıfırdan başlıyor. Yani
   * "kullanıcı en son ne zaman uğradı" sorusunun cevabı. Tek başına bir
   * oturumu sınırlamıyor (bkz. `SESSION_ABSOLUTE_TTL`).
   */
  JWT_REFRESH_TTL: z.string().default('7d'),

  /**
   * 🔴 MUTLAK oturum ömrü. Rotasyon bunu ileri kaydırmıyor.
   *
   * `JWT_REFRESH_TTL` her yenilemede sıfırlandığı için, her gün panele giren
   * bir kullanıcının oturumu SONSUZA kadar yaşıyordu: aylar önce girilmiş bir
   * tarayıcıdaki çerez hâlâ geçerliydi ve parola değişimi dışında onu
   * öldürecek hiçbir doğal olay yoktu. Bu değer, oturumun doğum tarihinden
   * itibaren sayılan sert bir tavan — dolduğunda kullanıcı ne kadar aktif
   * olursa olsun yeniden parola giriyor.
   */
  SESSION_ABSOLUTE_TTL: z.string().default('30d'),

  // AES-256-GCM anahtarı: 32 bayt = 64 hex karakter.
  TOKEN_VAULT_KEY: z
    .string()
    .regex(/^[0-9a-f]{64}$/i, 'TOKEN_VAULT_KEY 64 hex karakter olmalı (32 bayt)'),

  /**
   * Destek yazışmasının kasa anahtarı — `TOKEN_VAULT_KEY`den AYRI.
   *
   * 🔴 Neden ikinci bir anahtar: `TOKEN_VAULT_KEY` rotasyonu planlanmış bir
   * iş (jeton sızıntısı şüphesinde değişir) ve rotasyon betiği "tüm
   * jetonları eski anahtarla çöz, yenisiyle şifrele" yapıyor — destek
   * yazışmasını TANIMIYOR. Aynı anahtar paylaşılsaydı, bir jeton rotasyonu
   * tüm destek geçmişini kalıcı olarak okunamaz hâle getirirdi.
   *
   * OPSİYONEL ve boşsa `TOKEN_VAULT_KEY`e düşüyor (`resolveSupportVaultKey`):
   * yeni bir kurulumun iki anahtar üretmek zorunda kalmaması için. Düşüş
   * SESSİZ değil — servis açılışta hangi anahtarı kullandığını logluyor.
   *
   * ⚠️ Doldurulduktan SONRA değiştirilirse tüm destek yazışması çözülemez
   * hâle gelir. Değiştirilecekse önce eski anahtarla çözüp yenisiyle yeniden
   * şifreleyen bir geçiş gerekir — `TOKEN_VAULT_KEY` ile aynı kural.
   */
  SUPPORT_VAULT_KEY: z.preprocess(
    emptyToUndefined,
    z
      .string()
      .regex(/^[0-9a-f]{64}$/i, 'SUPPORT_VAULT_KEY 64 hex karakter olmalı (32 bayt)')
      .optional(),
  ),

  /**
   * Yeni destek talebi bildiriminin gittiği PLATFORM adresi.
   *
   * Personel bir çalışma alanının üyesi değil, yani kiracı bildirim
   * altyapısı ("zil") onlara ulaşamıyor — `Notification.workspaceId` zorunlu
   * (gerekçe `notification-backstage.bridge.ts`te). Kuyruğa yeni bir bilet
   * düştüğünde birinin haberi olmasının tek yolu bu adrese giden posta.
   *
   * Boşsa posta GİTMİYOR ve bu bir arıza değil eksik ayar: kuyruk ekranı
   * (`/admin/support/tickets`) zaten her şeyi gösteriyor, posta yalnız
   * "ekranın açık olmadığı" anı kapatıyor. Boş olduğunda uyarı bir kez
   * loglanıyor, her bilette değil.
   */
  SUPPORT_TEAM_EMAIL: z.preprocess(emptyToUndefined, z.email().optional()),

  // Faz 1+ — henüz başvuru sürecinde olabilir, o yüzden opsiyonel.
  META_APP_ID: z.string().optional(),
  META_APP_SECRET: z.string().optional(),
  /// 🔴 Marketing API v24.0 6 Eki 2026'da kapanıyor; v26 kırılımları 27 Eki
  /// 2026'da tüm sürümlere uygulanıyor (platform-specs/meta.md §0.1).
  /// Varsayılan v26.0 — `.env`teki eski sabit (v24.0) varsayılanı EZER, orası
  /// da güncellenmeli.
  META_API_VERSION: z.string().default('v26.0'),
  /**
   * Her Graph çağrısına `appsecret_proof` (HMAC-SHA256(app_secret, jeton))
   * eklenir. Uygulama panelinde "Require App Secret" açılmadan önce BU açık
   * olmalı; kapatmak yalnız teşhis içindir. Worker'ın senkron okuyucusu ve
   * Reklam Kütüphanesi istemcisi de gönderiyor (29 Eyl 2026). ⚠️ Kalan tek
   * kanıtsız çağrı: API `competitor/ad-library-config.service.ts`
   * `verifyToken` (yönetici jeton kaydı) — o da göndermeden "Require App
   * Secret" AÇILMAMALI.
   */
  META_APPSECRET_PROOF: z
    .enum(['true', 'false'])
    .default('true')
    .transform((value) => value === 'true'),
  /// Facebook Login for Business yapılandırma kimliği. OAuth akışı bunu
  /// `config_id` olarak gönderiyor; olmadan izin ekranı klasik kipte açılır.
  META_CONFIG_ID: z.string().optional(),
  /// İzin ekranına gönderilen geri dönüş adresi. Meta konsolundaki
  /// "Valid OAuth Redirect URIs" ile BİREBİR aynı olmak zorunda.
  /// Boşsa `API_PUBLIC_URL` üzerinden temiz yol üretilir; dolu olmasının tek
  /// sebebi bu alan adının önceki kurulumundan kalma kaydın hâlâ geçerli
  /// olması — kullanıcıya konsola girme işi çıkarmamak için.
  META_REDIRECT_URI: z.string().optional(),
  /**
   * Meta lead ÇEKME (`meta.lead.list`, `meta.leadform.stats`) — 29 Eyl 2026 gece
   * kullanıcı kararıyla geri geldi, varsayılan KAPALI. Açıkken klasik OAuth
   * kapsamına `leads_retrieval` (+ Sayfa kapsamları) eklenir; `META_CONFIG_ID`
   * kullanılıyorsa izin Meta App Dashboard'daki Login for Business
   * yapılandırmasına da ELLE eklenmeli (kod oradaki izin kümesini belirlemiyor).
   */
  META_LEADS_RETRIEVAL_ENABLED: z
    .enum(['true', 'false'])
    .default('false')
    .transform((value) => value === 'true'),
  /**
   * Meta reklam YORUMLARI (Faz 7, 30 Eyl 2026) — `meta.comment.*` (Facebook) ve
   * `meta.ig_comment.*` (Instagram): okuma, yanıt, gizle/göster, silme, beğeni,
   * yorumları kapatma, Sayfa küfür filtresi/engellenen kelimeler. Varsayılan
   * KAPALI. Açıkken klasik OAuth kapsamına Sayfa etkileşim + Instagram yorum
   * izinleri eklenir (`META_COMMENTS_SCOPES`); `META_CONFIG_ID` kullanılıyorsa
   * izinler Login for Business yapılandırmasına ELLE eklenmeli. Mevcut
   * bağlantılar kopmaz — yalnız yeniden bağlanana kadar yorum işlemleri
   * "izin eksik" der.
   */
  META_COMMENTS_ENABLED: z
    .enum(['true', 'false'])
    .default('false')
    .transform((value) => value === 'true'),

  GOOGLE_ADS_CLIENT_ID: z.string().optional(),
  GOOGLE_ADS_CLIENT_SECRET: z.string().optional(),
  GOOGLE_ADS_DEVELOPER_TOKEN: z.string().optional(),
  /// Yönetici (MCC) hesabı. Google Ads API'de alt hesaplara bu kimlik
  /// üzerinden erişiliyor; `login-customer-id` başlığı olarak gidiyor.
  GOOGLE_ADS_LOGIN_CUSTOMER_ID: z.string().optional(),
  /**
   * Worker'ın rakip açık artırma (Auction Insights) sorgusu — varsayılan KAPALI
   * (29 Eyl 2026). Google `metrics.auction_insight_*` alanlarını herkese açık
   * vermiyor (yalnız izin listesindeki geliştiricilere); açıkken her senkron
   * döngüsü proje kotasından 1 işlemi reddedilecek bir sorguya harcıyordu.
   * Google erişimi verirse `true` + worker restart.
   */
  GOOGLE_AUCTION_INSIGHTS_ENABLED: z
    .enum(['true', 'false'])
    .default('false')
    .transform((value) => value === 'true'),
  /**
   * Google Ads API erişim seviyesi (29 Eyl 2026, Faz 5) — seviye artık Cloud
   * PROJESİNE bağlı ve günlük işlem kotası TÜM kiracılar arasında paylaşılıyor:
   * EXPLORER 2.880/gün (üretim), BASIC 15.000/gün, STANDARD sınırsız. Proje
   * sayacı (`google:ops:<yyyy-mm-dd>`) bu tavana göre %80'de uyarı, %95'te
   * zorunlu olmayan okumaları durdurur. Cloud Console → Google Ads API → Access
   * ekranındaki seviye yazılır; boşsa en düşük (Explorer) varsayılır.
   */
  GOOGLE_ADS_ACCESS_TIER: z.enum(['EXPLORER', 'BASIC', 'STANDARD']).default('EXPLORER'),
  /**
   * Google Data Manager API (29 Eyl 2026, Faz 5) — varsayılan KAPALI.
   * Açıkken Google bağlantısı `https://www.googleapis.com/auth/datamanager`
   * kapsamını da ister (çevrimdışı dönüşüm, lead nitelendirme, Customer Match).
   * Açmadan önce: kapsam OAuth izin ekranında bildirilir ve Data Manager API
   * Cloud projesinde etkinleştirilir; sonra kullanıcılar Google'ı yeniden bağlar.
   */
  GOOGLE_DATAMANAGER_ENABLED: z
    .enum(['true', 'false'])
    .default('false')
    .transform((value) => value === 'true'),
  /**
   * İzin ekranına gönderilen geri dönüş adresi — `META_REDIRECT_URI` ile aynı
   * gerekçe ve aynı geçmiş.
   *
   * 🔴 Google Cloud Console'daki OAuth istemcisi eski kurulumdan (APEX
   * GrowthOS) kalma ve `GOOGLE_ADS_CLIENT_ID` onunla AYNI — yani "Authorized
   * redirect URIs" listesinde duran adres `/api/v1` önekli:
   *
   *     https://api.mentorivo.com/api/v1/integrations/google/callback
   *
   * Bizim API'de önek yok, o yüzden bu değişken olmadan kod temiz yolu üretir
   * ve Google `redirect_uri_mismatch` döner — ekran hangi parçanın yanlış
   * olduğunu SÖYLEMEZ, hata ayıklaması pahalıdır. Controller iki yolu da
   * dinlediği için geri dönüş yine karşılanıyor.
   */
  GOOGLE_REDIRECT_URI: z.string().optional(),

  /**
   * "Google ile giriş" — 23 Eyl 2026.
   *
   * İstemci Ads ile AYNI OAuth istemcisi (`.env`de iki ad da aynı değeri
   * taşıyor); ayrı ad, giriş istemcisini ileride ayırmayı tek satır yapmak
   * için. Boşsa `GOOGLE_ADS_CLIENT_*` kullanılır. İkisi de boşsa giriş ucu
   * `/login?error=google_unavailable`e döner, 500 atmaz.
   */
  GOOGLE_CLIENT_ID: z.string().optional(),
  GOOGLE_CLIENT_SECRET: z.string().optional(),
  /**
   * Giriş akışının geri dönüş adresi. Boşsa
   * `API_PUBLIC_URL` + `/auth/google/callback`.
   *
   * 🔴 Google Console'da "Authorized redirect URIs" listesinde BİREBİR
   * bulunmalı. Eski kurulumdan kalma kayıtlarda giriş için bir adres YOK
   * (23 Eyl 2026'da kontrol gruplu yoklandı) — bu satır Console'a elle
   * eklenir. Ads'in `/integrations/google/callback` adresi YENİDEN
   * KULLANILMAZ: o uç `state`i bağlantı akışının Redis anahtarında arıyor.
   */
  GOOGLE_LOGIN_REDIRECT_URI: z.string().optional(),

  TIKTOK_APP_ID: z.string().optional(),
  TIKTOK_APP_SECRET: z.string().optional(),
  /**
   * Genişleme Adım 4 — TikTok Ads.
   *
   * 🔴 Değer `.env`'de ZATEN VARDI, şemada tanımlı olmadığı için sessizce
   * DÜŞÜYORDU: `parseServerEnv` bilinmeyen anahtarı hata vermeden atıyor, yani
   * `env.TIKTOK_REDIRECT_URI` tipsiz `undefined`dı. Sonucu kod okunarak
   * görülmüyordu — yalnızca izin ekranı açıldığında `redirect_uri` uyuşmazlığı
   * olarak patlıyordu.
   *
   * TikTok konsolundaki kayıt `/api/v1` önekli:
   *
   *     https://api.mentorivo.com/api/v1/integrations/tiktok/callback
   *
   * Bizim API'de önek yok — bu değişken olmadan `OAuthService.redirectUri()`
   * öneksiz temiz yolu üretir ve TikTok izin ekranı hiç açılmaz. Controller
   * iki biçimi de dinliyor (`@Controller(['integrations','api/v1/integrations'])`),
   * dönüş yolu sorun değil; sorun GİDEN adresin birebir eşleşmesi.
   *
   * `META_REDIRECT_URI` ile aynı gerekçe, `LINKEDIN_REDIRECT_URI` gibi ZORUNLU
   * DEĞİL: burada boş kalırsa üretilen temiz yol yanlış olur ama uygulama
   * açılmaya devam etmeli — "eksik" kararını `PLATFORM_ENV_KEYS` veriyor.
   */
  TIKTOK_REDIRECT_URI: z.string().optional(),
  /**
   * `true` → TikTok bağlayıcısı (API) VE senkron okuyucusu (worker) Marketing
   * API'nin SANDBOX ana bilgisayarına gider: `https://sandbox-ads.tiktok.com/open_api/v1.3`
   * (Faz 6, 30 Eyl 2026; resmî "Sandbox accounts" sayfası doc 1738855331457026).
   *
   * Sandbox'ta OAuth YOK: jeton geliştirici portalında üretilir ve bağlantı
   * `/root/plesk-ops/mentorivo/tiktok-sandbox-connect.js` ile kurulur. Teslimat
   * yok, rapor verisi sahte (2020-12-08…19), uç başına 1 QPS / 30 QPM.
   *
   * ⚠️ Varsayılan KAPALI ve üretimde kapalı kalmalı: açıkken bağlı GERÇEK
   * hesapların jetonu sandbox'ta geçersizdir (40105) ve senkron her birini
   * "yeniden bağlan" durumuna düşürür. Değiştirince API + worker restart.
   */
  TIKTOK_SANDBOX: z
    .enum(['true', 'false'])
    .default('false')
    .transform((value) => value === 'true'),

  // ── Genişleme Adım 5 — LinkedIn Ads ─────────────────────────────────────
  //
  // Üçü de `.optional()`: anahtar yokken uygulama AÇILMAYA devam etmeli ve
  // kart "yönetici yapılandırmalı" demeli (Meta/Google deseni). Şemayı zorunlu
  // yapmak, LinkedIn kullanmayan bir kurulumda API'nin hiç açılmaması demekti.
  LINKEDIN_CLIENT_ID: z.string().optional(),
  LINKEDIN_CLIENT_SECRET: z.string().optional(),
  /**
   * 🔴 Bu değişken LinkedIn'de ZORUNLU sayılıyor (`PLATFORM_ENV_KEYS.LINKEDIN`),
   * Meta ve Google'da opsiyonel olduğu hâlde. Zod'da yine `.optional()` çünkü
   * boş olması uygulamayı açılmaz kılmamalı; "eksik" kararını registry veriyor.
   *
   * Fark şurada: Meta/Google'da değişken boşsa kodun ürettiği temiz yol
   * (`API_PUBLIC_URL` + `OAUTH_CALLBACK_PATH`) DOĞRU cevaptır. LinkedIn'de doğru
   * cevabı yalnızca Developer Portal'a bakan insan biliyor — eski proje kaydı
   * `/api/v1` önekli ve LinkedIn BİREBİR eşleşme arıyor. Yanlış tahmin, izin
   * ekranının hiç açılmaması demek ve hata hangi parçanın yanlış olduğunu
   * SÖYLEMİYOR. Boş bırakılıp "yapılandırılmamış" görünmesi buna yeğdir.
   */
  LINKEDIN_REDIRECT_URI: z.string().optional(),
  /**
   * `YYYYMM`. Boşsa adapter `LINKEDIN_DEFAULT_API_VERSION` (202609, API ve worker ortak) kullanıyor.
   *
   * 🔴 Koda gömülmemesinin sebebi: LinkedIn varsayılan sürüm uygulamıyor ve her
   * sürüm ~1 yıl sonra sunset oluyor (o gün API 426 dönüyor ve çözüm jeton
   * yenilemek değil deploy yapmak). Yılda bir kez zorunlu olan bu yükseltmenin
   * tek satırlık bir env değişikliğiyle çözülebilmesi gerekiyor. Eski proje
   * sabiti ALTI ayrı dosyaya kopyalamıştı.
   *
   * `PLATFORM_ENV_KEYS`e YAZILMIYOR — boş olması bir eksiklik değil, varsayılanı
   * kabul etmek demek.
   */
  LINKEDIN_API_VERSION: z.string().optional(),
  /**
   * LinkedIn'de AYRI BAŞVURU isteyen ürünler (Faz 2, 29 Eyl 2026). Her biri
   * izin ekranında istenen kapsam kümesini GENİŞLETİR:
   *   CONVERSIONS        → `rw_conversions` (Conversions API / CAPI akışı)
   *   MATCHED_AUDIENCES  → `rw_dmp_segments` (kişi/şirket listesi, yeniden hedefleme)
   *   EVENTS             → `r_events`, `rw_events` (Event Ads için etkinlik)
   *
   * 🔴 Varsayılan KAPALI, iki ayrı sebeple:
   *   1. Onaylanmamış bir ürünün kapsamını istemek izin ekranını HERKES için
   *      kırar ve bu yalnız tarayıcıda görünür.
   *   2. Kapsam kümesi değişince LinkedIn ÖNCEKİ TÜM JETONLARI geçersiz kılar
   *      (hafıza: linkedin-scope-degisimi-jetonlari-koparir) — bayrağı açmak,
   *      bağlı her kullanıcının yeniden bağlanması demek.
   * Açma sırası: ürün onayı → `.env` → `pm2 restart mentorivo-api` → tarayıcıda
   * bir bağlantı denemesi.
   *   LEAD_SYNC          → `r_marketing_leadgen_automation` (Lead Sync API — lead
   *                        formu YANITLARINI okumak; 29 Eyl 2026 gece kullanıcı
   *                        kararıyla kapsama geri döndü). Sonuç kişisel veri.
   */
  LINKEDIN_PRODUCT_CONVERSIONS: z
    .enum(['true', 'false'])
    .default('false')
    .transform((value) => value === 'true'),
  LINKEDIN_PRODUCT_MATCHED_AUDIENCES: z
    .enum(['true', 'false'])
    .default('false')
    .transform((value) => value === 'true'),
  LINKEDIN_PRODUCT_EVENTS: z
    .enum(['true', 'false'])
    .default('false')
    .transform((value) => value === 'true'),
  LINKEDIN_PRODUCT_LEAD_SYNC: z
    .enum(['true', 'false'])
    .default('false')
    .transform((value) => value === 'true'),
  /**
   * Faz 6 (30 Eyl 2026) — LinkedIn resmî API yüzeyinin geri kalanı. Aynı kural:
   * varsayılan KAPALI, açmak kapsam kümesini değiştirir (jetonlar ölür).
   *   ORG_ADMIN            → `rw_organization_admin` (Advertising API'nin parçası;
   *                          sayfa rolü isteme, organizationAuthorizations,
   *                          sayfa bildirim abonelikleri, yönetici sayfa ayrıntısı)
   *   COMMUNITY_MANAGEMENT → `r/w_organization_social_feed`, `r_organization_followers`
   *                          (AYRI ürün: yorum/tepki yönetimi, takipçi araması)
   *   MEMBER_ANALYTICS     → `r_member_postAnalytics`, `r_member_profileAnalytics`
   *   MEDIA_PLANNING       → `rw_media_plans` (kayıtlı medya planları)
   */
  LINKEDIN_PRODUCT_ORG_ADMIN: z
    .enum(['true', 'false'])
    .default('false')
    .transform((value) => value === 'true'),
  LINKEDIN_PRODUCT_COMMUNITY_MANAGEMENT: z
    .enum(['true', 'false'])
    .default('false')
    .transform((value) => value === 'true'),
  LINKEDIN_PRODUCT_MEMBER_ANALYTICS: z
    .enum(['true', 'false'])
    .default('false')
    .transform((value) => value === 'true'),
  LINKEDIN_PRODUCT_MEDIA_PLANNING: z
    .enum(['true', 'false'])
    .default('false')
    .transform((value) => value === 'true'),

  // ── Genişleme Adım 6 — X (Twitter) Ads ──────────────────────────────────
  //
  // 🔴 OAuth **1.0a**. Diğer dört platformun tamamı OAuth 2.0 kullanıyor ve
  // bunun env üzerindeki görünür sonucu isimlendirme: 1.0a'da "client" değil
  // **consumer** var, ve `_SECRET` bir istemci sırrı değil bir İMZA
  // ANAHTARIDIR — her isteğin HMAC-SHA1 imzasına giriyor, gövdede hiç
  // gönderilmiyor. `TWITTER_CLIENT_ID` diye yeniden adlandırmak, sonraki
  // okuyanı "neden `client_secret` gövdede yok" diye aratırdı.
  //
  // ⚠️ Consumer anahtarları X portalında REGENERATE EDİLİRSE mevcut Access
  // Token'lar da kopar — kayıtlı bütün kullanıcı bağlantıları ölür.
  TWITTER_CONSUMER_KEY: z.string().optional(),
  TWITTER_CONSUMER_SECRET: z.string().optional(),
  /**
   * 🔴 `TWITTER_REDIRECT_URI` BİLEREK YOK — ve bu, Meta/Google/TikTok/LinkedIn
   * dördünden de AYRILDIĞIMIZ tek yer.
   *
   * Orada değişken var çünkü portala kayıtlı adresin ne olduğu sunucudan
   * doğrulanamıyordu (eski kurulumdan kalma `/api/v1` önekli kayıtlar). X'te
   * bu SORU CEVAPLANDI: 1.0a'nın ilk adımı (`POST oauth/request_token`)
   * callback adresini SUNUCU TARAFINDA doğruluyor ve kayıtlı değilse
   * `403 code=415 "Callback URL not approved"` döndürüyor. 14 Ağu 2026'da
   * ölçüldü — kodun ürettiği temiz yol
   *
   *     https://api.mentorivo.com/integrations/twitter/callback
   *
   * `oauth_callback_confirmed=true` ile kabul edildi, kayıtlı olmayan bir
   * adres aynı çağrıda reddedildi (kontrol grubu). Yani bir env değişkeni
   * eklemek, ölçülmüş bir gerçeğin üstüne elle ezme imkânı koymak olurdu.
   */
  /**
   * `POST oauth/access_token` sonrası jeton SAHİBİNİ okumak için gereken
   * uygulama sahibi jetonları DEĞİL — bunlar yalnız YOKLAMA/doğrulama içindir.
   *
   * 🔴 Kullanıcı bağlantıları OAuth akışından gelir, BUNLARDAN DEĞİL. Kodun
   * hiçbir yeri bu ikisini okumuyor; `.env`'de durmalarının tek sebebi
   * `scripts`/elle yoklama. Şemaya yazılmalarının sebebi de tam tersi bir
   * kaza: şemada olmayan bir anahtar `parseServerEnv` tarafından SESSİZCE
   * atılıyor (TikTok'ta `TIKTOK_REDIRECT_URI` ile birebir bu yaşandı) ve
   * "`.env`'de var ama `env.X` undefined" hatası kod okunarak görülmüyor.
   */
  TWITTER_OWNER_ACCESS_TOKEN: z.string().optional(),
  TWITTER_OWNER_ACCESS_TOKEN_SECRET: z.string().optional(),
  /// Yoklama hesabı (`18ce55wz77k`). Üretim yolunda KULLANILMIYOR.
  TWITTER_TEST_ACCOUNT_ID: z.string().optional(),
  /**
   * Ads API sürümü. Boşsa adapter `TWITTER_DEFAULT_API_VERSION` (12) kullanır.
   *
   * `LINKEDIN_API_VERSION` ile AYNI gerekçe ve aynı ölçüm: sürüm numarası
   * isteğin YOLUNUN İÇİNDE ve eskiyen sürüm kapatılıyor. 14 Ağu 2026'da
   * ölçüldü: `/11` hâlâ 200 dönüyor, `/13` ise
   * `403 "Version 13.0 is not available."` — yani 13 açıldığı gün bu tek
   * satırla geçilebilmeli, deploy ile değil.
   *
   * ⚠️ Ana bilgisayar adı BİLEREK yazılmadı: adapter sınırı denetimi
   * (`grep`) X'e özgü sözcüklerin `connector/twitter/` dışında geçmediğini
   * ölçüyor ve bir yorum da o denetimi kırar. Adres `twitter.client.ts`te.
   *
   * `PLATFORM_ENV_KEYS`e YAZILMIYOR: boş olması bir eksiklik değil,
   * varsayılanı kabul etmek demek (`LINKEDIN_API_VERSION` ile aynı karar).
   */
  TWITTER_API_VERSION: z.string().optional(),
  /**
   * `true` → X adapter'ı Ads API'nin SANDBOX ana bilgisayarına gider (Faz 2,
   * 29 Eyl 2026). Yalnız ileride uçtan uca deneme için; sandbox'ta hiçbir şey
   * yayına çıkmaz ve hesap/fon kaynağı yalnız orada oluşturulabilir.
   *
   * ⚠️ Varsayılan KAPALI ve üretimde kapalı kalmalı: açıkken bağlı gerçek
   * hesap sandbox'ta bulunmaz. Worker okuyucusu bayrağı ancak
   * `sync.service.ts` geçirirse görür (bkz. `twitter.client.ts`).
   */
  TWITTER_ADS_SANDBOX: z
    .enum(['true', 'false'])
    .default('false')
    .transform((value) => value === 'true'),

  // ── Genişleme Adım 1 — Shopify (mağaza) ─────────────────────────────────
  //
  // 🔴 Bunlar REKLAM platformu anahtarı DEĞİL. Shopify bir mağaza platformu;
  // `StorePlatform` enum'ında, `AdPlatform`ta değil. `PLATFORM_ENV_KEYS`e
  // yazılmıyorlar — o tablo `Record<AdPlatform, …>` ve yönetici panelindeki
  // reklam platformu kartlarını besliyor. Mağaza kartlarının karşılığı
  // `STORE_PLATFORM_ENV_KEYS`.
  //
  // Değerler eski projenin `.env`'inde VAR (`SHOPIFY_API_KEY` /
  // `SHOPIFY_API_SECRET`) ama bugünküne TAŞINMADI — kullanıcı taşıyacak.
  // Boşken adapter `available: false` bildiriyor ve kart "yönetici
  // yapılandırmalı" diyor; sessizce çalışmıyor gibi görünmüyor.
  /**
   * ETSY — uygulama düzeyi anahtar (keystring), kiracı başına DEĞİL.
   *
   * 🔴 Kimlik blob'una girmez ve girmemeli: her satıcı aynı keystring'i
   * kullanıyor, bizim uygulamamızın kimliği o. Blob'a koysaydık aynı değer
   * yüzlerce satırda şifreli olarak tekrar eder ve döndürmek her satırı
   * yeniden yazmayı gerektirirdi.
   *
   * ⚠️ Etsy jeton ucu `client_secret` KABUL ETMİYOR (PKCE) — yani bu tek
   * değer hem izin ekranı hem jeton yenileme için yeterli.
   */
  ETSY_KEYSTRING: z.string().optional(),
  /**
   * AMAZON SP-API — üçü de UYGULAMA düzeyi (kiracı başına değil).
   *
   * `AMAZON_SP_APP_ID` izin ekranının adresini kuruyor; LWA çifti jeton
   * takası ve yenileme için. Üçü birden dolu değilse Amazon kartı
   * "yönetici yapılandırmalı" durumunda kalıyor — `isImplemented` ile
   * KARIŞTIRILMAMALI: adapter yazıldı, eksik olan yapılandırma.
   */
  AMAZON_SP_APP_ID: z.string().optional(),
  AMAZON_LWA_CLIENT_ID: z.string().optional(),
  AMAZON_LWA_CLIENT_SECRET: z.string().optional(),
  SHOPIFY_API_KEY: z.string().optional(),
  SHOPIFY_API_SECRET: z.string().optional(),
  /// İzin ekranına gönderilen geri dönüş adresi. Shopify Partner panelindeki
  /// "Allowed redirection URL(s)" listesiyle BİREBİR aynı olmak zorunda —
  /// `META_REDIRECT_URI` ile aynı gerekçe. Boşsa `API_PUBLIC_URL` üzerinden
  /// temiz yol üretiliyor.
  SHOPIFY_REDIRECT_URI: z.string().optional(),

  ANTHROPIC_API_KEY: z.string().optional(),
  /// fal.ai — Flux görsel üretimi (tasarım §4.4, sağlayıcı seçimi Faz 4'te
  /// kesinleşecek). Eski kurulumdan devralındı, `CreativeProvider`
  /// soyutlamasının arkasında kalacak.
  FAL_KEY: z.string().optional(),

  /**
   * ÖDEME SAĞLAYICISI: İYZİCO (Faz 8, 12 Ağu 2026).
   *
   * Öncesinde burada `STRIPE_SECRET_KEY` / `STRIPE_WEBHOOK_SECRET` duruyordu.
   * Stripe Türkiye'de yerel kart ve taksit desteği vermiyor; ürünün ilk pazarı
   * Türkiye olduğu için sağlayıcı iyzico seçildi ve o iki değişken SİLİNDİ —
   * ölü bir env anahtarı bırakmak, altı ay sonra ".env'de Stripe var, demek ki
   * bir yerde kullanılıyor" yanılgısını doğururdu.
   *
   * 🔴 Üç değişken de OPSİYONEL ve bugün .env'de YOK. Anahtar yokken
   * `BillingModule` `NullPaymentProvider`ı devreye alıyor ve ödeme uçları
   * tipli `PAYMENT_NOT_CONFIGURED` (HTTP 503) dönüyor. Anahtarı kullanıcı
   * kendi yapıştıracak; hiçbir kod .env'e yazmıyor.
   *
   * `IYZICO_SECRET_KEY` İKİ İŞ birden yapıyor: giden isteğin `IYZWSv2`
   * imzasının HMAC anahtarı VE gelen webhook'un `X-IYZ-SIGNATURE-V3`
   * doğrulamasının anahtarı. Ayrı bir webhook sırrı YOK (Stripe'ın aksine) —
   * dördüncü bir değişken tanımlamak, doldurulmadığında webhook'u sessizce
   * reddeden bir tuzak olurdu.
   */
  IYZICO_API_KEY: z.string().optional(),
  IYZICO_SECRET_KEY: z.string().optional(),
  /// Sandbox varsayılan: gerçek para hareketi ancak bilinçli olarak
  /// `https://api.iyzipay.com` yazılınca başlar. Ters varsayılan (production)
  /// yanlış yapılandırmayı gerçek tahsilata çevirirdi.
  IYZICO_BASE_URL: z.string().default('https://sandbox-api.iyzipay.com'),

  /// Ajan modeli. Kalıcı bir ayar deposu (PlatformSetting) olmadığı için
  /// model seçimi bugün env'den geliyor; `/admin/ai` onu SALT OKUNUR gösterir.
  /// 29 Eyl 2026: varsayılan Opus 5.5 (sohbet + operatör; sağlayıcı modelin
  /// yeteneğine göre istek kuruyor — `anthropic.llm.provider.ts`).
  AGENT_MODEL: z.string().default('claude-opus-5-5'),
  /**
   * AJANIN WEB ARAŞTIRMASI — kapatma anahtarı (1 Eki 2026).
   *
   * Açıkken sohbet ajanı ve arka plan operatörü isteğe Anthropic'in sunucu
   * araçlarını (`web_search`, `web_fetch`) ekliyor; günlük tavan ve kuruluşun
   * Console ayarı ayrıca uygulanıyor (`AgentWebToolsService`). `false` →
   * araçlar hiç gitmez ve istem web araştırmasından söz etmez. Değişiklik
   * API restart ister (env sıcak yüklenmiyor).
   */
  AGENT_WEB_TOOLS_ENABLED: z
    .enum(['true', 'false'])
    .default('true')
    .transform((value) => value === 'true'),
  /**
   * AJANIN KOD ÇALIŞTIRMASI — kapatma anahtarı (Faz 9 X1, 1 Eki 2026).
   *
   * Açıkken sohbet ajanı ve arka plan operatörü isteğe Anthropic'in sunucu
   * tarafı sandbox'ını (`code_execution_20260521`) ekliyor; sohbette ayrıca
   * hazır belge becerileri (xlsx/pptx/docx/pdf), kullanıcı eklerinin Files
   * API'ye bir kez yüklenmesi ve okuma araçlarının kod içinden çağrılması
   * (programatik araç çağırma) bu anahtara bağlı. Günlük tavan ve kuruluşun
   * Console ayarı ayrıca uygulanıyor (`AgentCodeToolsService`). `false` →
   * hiçbiri gitmez, istem kod çalıştırmadan söz etmez, ekler bugünkü gibi
   * (base64) gider. Değişiklik API restart ister.
   */
  AGENT_CODE_EXECUTION_ENABLED: z
    .enum(['true', 'false'])
    .default('true')
    .transform((value) => value === 'true'),
  /**
   * ═══════════════════════════════════════════════════════════════════════
   * FAZ 9 · X2 — bağlam, verim ve bellek (1 Eki 2026)
   * ═══════════════════════════════════════════════════════════════════════
   * Hepsi API restart ister (env sıcak yüklenmiyor). Sağlayıcı her birini
   * yalnız modelin desteklediği durumda gönderiyor (`modelCapabilities`);
   * desteklemeyen modelde anahtar sessizce etkisiz.
   *
   * SUNUCU TARAFI SIKIŞTIRMA (`compact_20260112`) — konuşma bağlam bütçesini
   * aşınca eski kısım sağlayıcıda özetleniyor; özet bloğu `mentorivo_raw`da
   * birebir saklanıp geri gidiyor. Açıkken satır penceresi sıkıştırma
   * bloğundan başlıyor (ilişki: `replayWindowStart`). `false` → eski 60/20
   * satır penceresi aynen.
   */
  AGENT_COMPACTION_ENABLED: z
    .enum(['true', 'false'])
    .default('true')
    .transform((value) => value === 'true'),
  /**
   * 1M BAĞLAM BETASI (`context-1m-2025-08-07`) — YALNIZ varsayılan penceresi
   * 200K olan modellerde anlamlı (Sonnet 4/4.5); Opus 5.5 / 4.6+ zaten 1M ve
   * başlığa ihtiyaç duymuyor. `auto` = ön sayım (`countTokens`) isteğin 200K'yı
   * aştığını söylerse; `on` = her istekte; `off` = hiç.
   */
  AGENT_CONTEXT_1M: z.enum(['off', 'auto', 'on']).default('auto'),
  /**
   * UZUN ÇIKTI — sohbet turunun çıktı tavanı 32K → modelin tavanı (128K).
   * Tavan maliyet değil emniyet (harcamayı efor belirliyor). `auto` = büyük
   * rapor / kapsamlı analiz isteyen tur ya da bir önceki cevabı tavana takılan
   * sohbet; `on` = her tur; `off` = hiç. Operatörün tavanı sabit (48K).
   * 300K (`output-300k-2026-03-24`) YALNIZ Message Batches'te: batch isteği
   * modelin akış tavanını (128K) aşan çıktı isterse sağlayıcı betayı ekliyor
   * (`toBatchRequest`); bugün hiçbir koşu o kadar çıktı istemiyor.
   */
  AGENT_LONG_OUTPUT: z.enum(['off', 'auto', 'on']).default('auto'),
  /**
   * DANIŞMAN MODEL (`advisor_20260301`) — boşsa KAPALI. Yürütücü `AGENT_MODEL`
   * (değişmiyor); zor/riskli kararda yürütücü bu modele danışıyor. Eşleşme
   * geçersizse (danışman yürütücüden zayıf) araç hiç gönderilmiyor ve
   * açılışta uyarı yazılıyor (`advisorPairingValid`).
   */
  AGENT_ADVISOR_MODEL: z.string().default(''),
  /**
   * HIZLI KİP (`fast-mode-2026-02-01`, `speed: "fast"`) — yalnız SOHBET
   * turlarında; operatörde ve batch'te hiç. Fiyat 2×; varsayılan kapalı.
   * Hızlı kipin kotası (429) dolarsa istek standart hızla tekrarlanıyor.
   */
  AGENT_FAST_MODE: z
    .enum(['true', 'false'])
    .default('false')
    .transform((value) => value === 'true'),
  /**
   * OPERATÖRÜN ACİL OLMAYAN KOŞULARI MESSAGE BATCHES İLE (%50) — devriye ve
   * tekrarlayan takipler (RECURRING/WATCH); olay, kullanıcı ve tek seferlik
   * takip koşuları HİÇ. Koşunun ilk model adımı batch'te; araç gerekirse koşu
   * aynı geçmişle normal yolda sürüyor (`operator-batch.ts`). Varsayılan
   * kapalı: koşu dakikalarca (en çok `OPERATOR_BATCH.maxWaitMinutes`) gecikebilir.
   */
  AGENT_OPERATOR_BATCH: z
    .enum(['true', 'false'])
    .default('false')
    .transform((value) => value === 'true'),
  /**
   * ÖNBELLEK TEŞHİSİ (`cache-diagnosis-2026-04-07`) — YALNIZ kayıt: her
   * yanıtın `diagnostics.cache_miss_reason`ı log'a yazılıyor (önek nerede
   * kırıldı). Geliştirme ayarı; davranış değişmiyor.
   */
  AGENT_CACHE_DIAGNOSIS: z
    .enum(['true', 'false'])
    .default('false')
    .transform((value) => value === 'true'),
  /**
   * BELLEK ARACI (`memory_20250818`) — `/memories` dizini BİZİM
   * veritabanımızda (`agent_memory_files`): çalışma alanı belleği (yönetici
   * yazar), kişisel bellek (yalnız sahibi), ortak notlar (salt okuma görünüm).
   * `false` → araç hiç gitmez; `remember`/`list_notes` aynen çalışır.
   */
  AGENT_MEMORY_TOOL_ENABLED: z
    .enum(['true', 'false'])
    .default('true')
    .transform((value) => value === 'true'),
  /**
   * ÜRÜN FOTOĞRAFI OKUMA MODELİ — `AGENT_MODEL`den AYRI ve bilerek daha hızlı.
   *
   * ═══════════════════════════════════════════════════════════════════════
   * Neden ikinci bir model ayarı
   * ═══════════════════════════════════════════════════════════════════════
   * "Aynı sorunun iki cevabı olmasın" kuralı (`anthropic-text.provider.ts` →
   * `FALLBACK_MODEL`) burada geçerli DEĞİL, çünkü soru aynı değil:
   *
   *   · Konsept üretimi bir YARATIM işi — üç farklı reklam fikri kurup
   *     aralarında seçim yapılabilir hâle getiriyor. Büyük model orada
   *     karşılığını veriyor.
   *   · Ürün okuması bir AYIKLAMA işi — fotoğrafta ne olduğunu adlandırıp
   *     strict şemaya dolduruyor. Şema zaten grameri kısıtlıyor, yani çıktının
   *     ŞEKLİ modele bırakılmış değil.
   *
   * Kullanıcının ölçtüğü şey buydu: sihirbazda tek bir cümle için Opus
   * bekletiyordu. Okuma kullanıcının GÖZÜNÜN ÖNÜNDE koşuyor (sihirbaz açık,
   * düğmeye basılmış) — orada saniyeler görünür, konsept üretiminde değil.
   *
   * ⚠️ Değiştirilecekse ARAÇ KULLANIMINI ve GÖRSEL girdiyi destekleyen bir
   * model olmalı; ikisi de zorunlu (`strict: true` + `image` bloğu).
   */
  VISION_READING_MODEL: z.string().default('claude-haiku-4-5-20251001'),
  /**
   * SOHBET BAŞLIĞI MODELİ — ajanın konuşmalarına ad koyan küçük model.
   *
   * İş tek satırlık bir özet (Cursor/Devin'deki sohbet adı gibi) ve ana
   * turla PARALEL koşuyor; büyük modelin bekletmesi ve fiyatı burada hiçbir
   * şey kazandırmıyor. Araç ya da görsel kullanılmıyor — değiştirilecekse
   * düz metin üreten herhangi bir model yeter.
   */
  AGENT_TITLE_MODEL: z.string().default('claude-haiku-4-5'),

  S3_ENDPOINT: z.string().optional(),
  S3_REGION: z.string().default('auto'),
  S3_BUCKET: z.string().optional(),
  S3_ACCESS_KEY_ID: z.string().optional(),
  S3_SECRET_ACCESS_KEY: z.string().optional(),
  /// İmzalı URL'lerin kökü (R2 özel alan adı). Boşsa `S3_ENDPOINT` üzerinden
  /// imzalanır. Bucket ASLA public değil — bu yalnızca imzanın konacağı ana
  /// bilgisayar adı.
  S3_PUBLIC_BASE_URL: z.string().optional(),

  /// PDF üretimi için headless Chrome ikilisinin yolu. Boşsa rapor PDF ucu
  /// `PDF_NOT_CONFIGURED` döner — sessizce boş dosya üretmez.
  PDF_RENDER_BIN: z.string().optional(),

  // Posta gönderimi Yandex 360 üzerinden: mentorivo.com'un MX'i zaten
  // mx.yandex.net ve SPF `redirect=_spf.yandex.net` diyor. Bu sunucudan
  // doğrudan göndermek SPF'i geçemez, DMARC `p=quarantine` olduğu için de
  // doğrulama maili spam'e düşer. Yandex üzerinden gönderince hizalı olur.
  //
  // SMTP_HOST boşsa mail gerçekten gönderilmez; bağlantı sunucu loguna yazılır.
  // Geliştirme sırasında kimlik bilgisi olmadan akışı test edebilmek için.
  SMTP_HOST: z.string().optional(),
  SMTP_PORT: z.coerce.number().int().default(465),
  SMTP_SECURE: z
    .string()
    .default('true')
    .transform((v) => v !== 'false'),
  SMTP_USER: z.string().optional(),
  SMTP_PASS: z.string().optional(),
  // Gönderen adresi SMTP_USER'dan farklı olabilir (takma ad), ikisi de
  // aynı alan adında olmalı yoksa DKIM hizalaması bozulur.
  SMTP_FROM: z.string().default('Mentorivo <noreply@mentorivo.com>'),

  // ═════════════════════════════════════════════════════════════════════════
  // BACKSTAGE — platform yönetim paneli
  // ═════════════════════════════════════════════════════════════════════════

  /// Panelin tarayıcıdan görünen kökü. Yalnızca bu host'tan gelen istekler
  /// backstage rotalarını görüyor (`proxy.ts` host bağı) ve TOTP QR kodundaki
  /// hesap etiketi buradan üretiliyor.
  BACKSTAGE_URL: z.url().default('https://backstage.mentorivo.com'),

  /// 🔴 Ürün JWT'sinden AYRI sır. Aynı olsaydı `JWT_SECRET` sızdığında panel
  /// de açılırdı — oysa panelin tek amacı ürün tarafındaki bir sızıntının
  /// buraya ULAŞMAMASI. Panel oturumu jeton değil opaque bir dize kullanıyor
  /// ama bu sır oturum jetonunun HMAC'inde ve TOTP zorluk anahtarında geçiyor.
  BACKSTAGE_SECRET: z.string().min(32, 'BACKSTAGE_SECRET en az 32 karakter olmalı'),

  /// Acil durum ağı — panelden SİLİNEMEZ, IP kural listesine her zaman
  /// eklenir. Virgülle ayrılmış CIDR listesi.
  ///
  /// Neden var: IP zorlaması açıkken yanlış bir kural yazmak yöneticiyi kendi
  /// panelinden kilitler ve düzeltmenin tek yolu sunucuya SSH ile girip
  /// veritabanını elle düzenlemek olur. Bu değer o senaryoyu ortadan
  /// kaldırıyor — sunucunun kendi IP'si ve yöneticinin sabit hattı burada
  /// durur.
  BACKSTAGE_BREAKGLASS_CIDR: z.string().default('127.0.0.1/32'),

  /// 🔴 API ile web arasındaki iç kapı.
  ///
  /// Backstage uçları api.mentorivo.com üzerinden de ERİŞİLEBİLİR durumda
  /// (aynı Nest süreci, aynı port). Bu başlık olmasaydı bir saldırgan
  /// `https://api.mentorivo.com/backstage/auth/login` adresine doğrudan
  /// istek atıp `x-backstage-ip` başlığını beyaz listedeki bir adresle
  /// doldurabilir ve IP kontrolünü TAMAMEN atlayabilirdi — çünkü istemci
  /// IP'si o başlıktan okunuyor.
  ///
  /// Sırrı yalnızca web sürecindeki route handler biliyor (kökteki .env'den
  /// okuyor, tarayıcıya İNMİYOR). nginx tarafında ayrıca api.mentorivo.com
  /// için `/backstage` yolu kapatılıyor — iki kemer.
  BACKSTAGE_PROXY_SECRET: z.string().min(32, 'BACKSTAGE_PROXY_SECRET en az 32 karakter olmalı'),

  /**
   * 🔴 DEMO İÇİN 2FA ATLAMA — CANLIDA ASLA DOLU OLMAMALI
   *
   * Doluysa panel girişinin ikinci adımı bu sabit kodu da kabul ediyor.
   * Yani TOTP'nin eklediği koruma ORTADAN KALKIYOR ve panelin tek kilidi
   * parola oluyor — bu panel tüm kiracıların verisini okuyor.
   *
   * Üç kemer var, üçü de bilerek:
   *   1. `NODE_ENV === 'production'` iken bu değer YOK SAYILIYOR (kod, .env
   *      değil, karar veriyor).
   *   2. Panelin üstünde kalıcı bir uyarı şeridi beliriyor —
   *      `TOTP_BYPASS_ACTIVE`. Görmezden gelinemez.
   *   3. Her kullanım denetim akışına `bypass: true` olarak yazılıyor.
   *
   * Kapatmak için: bu satırı .env'den SİL (boş bırakmak da yeter) ve API'yi
   * yeniden başlat. Kapatıldığında 2FA kurulmamış hesaplar bir sonraki
   * girişte QR ekranına düşüyor — ek bir işlem gerekmiyor.
   */
  BACKSTAGE_DEV_TOTP_BYPASS: z.string().optional(),

  /// nginx allow/deny bloğunun yazılacağı dosya. Boşsa nginx senkronu
  /// tamamen kapalı (uygulama katmanı yine çalışır).
  /// ⚠️ Bu YALNIZCA allow/deny satırlarını taşıyan bir `include` dosyası —
  /// vhost'un kendisi DEĞİL. Uygulama proxy yapılandırmasına hiç dokunmuyor:
  /// vhost_nginx.conf'u yazsaydı, bozuk tek bir yazım `proxy_pass` satırını
  /// da götürür ve panel ağ katmanında tamamen kapanırdı. Şimdi en kötü
  /// durumda yalnızca erişim listesi bozulur ve `nginx -t` onu da yakalar.
  BACKSTAGE_NGINX_CONF: z
    .string()
    .default('/var/www/vhosts/system/backstage.mentorivo.com/conf/backstage-allow.conf'),
});

export type ServerEnv = z.infer<typeof serverEnvSchema>;

// NodeJS.ProcessEnv yerine yapısal tip: bu paket frontend'de de derleniyor,
// @types/node'a bağlanmamalı.
export function parseServerEnv(
  raw: Record<string, string | undefined> | Record<string, unknown>,
): ServerEnv {
  const result = serverEnvSchema.safeParse(raw);
  if (!result.success) {
    const issues = result.error.issues
      .map((i) => `  - ${i.path.join('.')}: ${i.message}`)
      .join('\n');
    throw new Error(`Ortam değişkenleri geçersiz:\n${issues}`);
  }
  return result.data;
}
